import { defineStore } from "pinia";
import { WSClient } from "@/ems/WSClient";
import { Role } from "@/ems/perms";
import { LoggedUser, User } from "@/types/user";
import jwtDecode, { JwtPayload } from "jwt-decode";
import { computed, ref } from "vue";

export const userSessionStore = defineStore(
  "user_session",
  () => {
    // State
    const logged_user = ref<LoggedUser>({} as LoggedUser);
    const token = ref<string>("");
    const status = ref<"" | "loading" | "error" | "success">("");
    const roles = ref<Role[] | undefined>(undefined);
    const organization = ref<any>();
    const docx_options = ref<Array<string>>([]);
    // Getters
    const isLoggedIn = computed((): boolean => {
      return !!logged_user.value?.UserId;
    });
    const user = computed((): User => {
      const { UserId, Email, FirstName, LastName } = logged_user.value;
      return { UserId, Email, FirstName, LastName } as User;
    });
    // Actions

    async function session_check(client: WSClient, force_query: boolean): Promise<LoggedUser | null> {
      if (isLoggedIn.value && !force_query) {
        const current_time = Date.now() / 1000;
        const decodedToken = jwtDecode<JwtPayload>(token.value);
        const token_expiration = decodedToken.exp ? decodedToken.exp : 0;
        if (token.value) {
          if (token_expiration > current_time) {
            console.log("Session check passed with JWT");
            return logged_user.value;
          } else {
            console.warn("JWT expired");
          }
        } else {
          console.warn("No WT for session check");
        }
      }

      status.value = "loading";
      try {
        const res = await client.queryWs<{ jwt: string; logged_user: LoggedUser | null }>("GET", "session_login");
        if (res.logged_user) {
          on_auth_success(res.jwt, res.logged_user);
          return res.logged_user;
        }
        on_auth_error();
      } catch (error) {
        on_auth_error();
      }
      return null;
    }

    async function logout(client: WSClient) {
      reset_logout();
      client.queryWs("POST", "session_logout");
    }

    function on_auth_error() {
      console.error("Auth error");
      status.value = "error";
      reset_logout();
    }

    function on_auth_success(jwt: string, user: LoggedUser) {
      status.value = "success";
      token.value = jwt;
      logged_user.value = user;
      docx_options.value = user.ui_prefs?.docx_options ?? [];
    }

    function clear_storages() {
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const key = sessionStorage.key(i);
        if (key !== null && key !== "afterlogin") {
          sessionStorage.removeItem(key);
        }
      }
      localStorage.clear();
    }

    function reset_logout() {
      status.value = "";
      token.value = "";
      organization.value = undefined;
      roles.value = undefined;
      logged_user.value = {} as LoggedUser;
      document.cookie = "PHPSESSID=; Max-Age=0";
      clear_storages();
      docx_options.value = [];
    }

    async function get_user_roles(client: WSClient = new WSClient()): Promise<Role[]> {
      if (roles.value === undefined) {
        const resp = await client.queryWs<{ roles: Role[] }>("GET", "user_UserRole");
        roles.value = resp.roles;
      }
      return roles.value;
    }

    return {
      logged_user,
      user,
      token,
      status,
      roles,
      isLoggedIn,
      session_check,
      on_auth_success,
      logout,
      get_user_roles,
      organization,
      docx_options,
      reset_logout
    };
  },
  {
    persist: {
      storage: localStorage,
    },
  }
);
